/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'sharedUi/Button';
import { valueSetsService } from '../../services/value-sets.service';
import { referencableIn } from '../../hooks/useProjectScope';
import { usersService } from '../../services/users.service';
import { personnelLookupService } from '../../services/personnel-lookup.service';
import { recipientListsService } from '../../services/recipient-lists.service';
import { useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import type { Party } from '../../services/notification-engine.types';

/**
 * Edits a list of parties — used by recipient lists and by STATIC recipients.
 *
 * Each party type carries exactly ONE kind of identifier, which is why the row
 * swaps its input rather than showing every field at once: a party naming both
 * a user and a list is rejected on save, and offering both invites it.
 *
 * Identifiers are chosen from a picker rather than typed. They are ObjectIds —
 * nobody knows them by heart, and a typo produced a party that saved cleanly and
 * resolved to nobody at send time.
 *
 * **Each picker is gated on permission to read that directory.** Without it the
 * row falls back to the raw id field: someone who may not browse the staff
 * directory can still enter an id they were given, and the API remains the real
 * gate either way. It also means no pointless request is made for a list the
 * caller would only be refused.
 */
interface PartyEditorProps {
  value: Party[];
  onChange: (parties: Party[]) => void;
  /** Labels the group being edited, e.g. "Suppressed endpoints". */
  label?: string;
  hint?: string;
  /**
   * Where these parties live. A recipient LIST may not contain another list —
   * the API refuses it — so `list` drops LIST from the type options rather than
   * offering a choice that cannot be saved.
   */
  context?: 'list' | 'recipient';
  /** The project this belongs to — only its own and shared recipient lists may
   *  be referenced, which is what the API accepts. */
  projectId?: string | null;
  testId?: string;
}

const BLANK: Party = { partyType: 'USER', userId: '' };

const PartyEditor = ({
  value, onChange, label = 'Parties', hint,
  context = 'recipient', projectId, testId = 'PartyEditor',
}: PartyEditorProps) => {
  const canReadUsers = useHasPermission(PERMISSIONS.USER_READ);
  const canReadPersonnel = useHasPermission(PERMISSIONS.PERSONNEL_READ);
  const canReadLists = useHasPermission(PERMISSIONS.RECIPIENT_LIST_READ);

  const { data: partyVS } = useQuery({
    queryKey: ['vs', 'partyType'],
    queryFn: () => valueSetsService.getByKey('partyType', 'en'),
    staleTime: 3e5,
  });

  /* Scoped to the project this audience belongs to — a party is a PERSON, and
     naming someone from another project is how one tenant's staff end up
     receiving another tenant's messages. The API intersects `appId` with the
     caller's own scope, so this narrows, never widens. */
  const { data: usersPage } = useQuery({
    queryKey: ['users', 'partyPicker', projectId || ''],
    queryFn: () => usersService.getAll(
      { page: 1, page_size: 500, appId: projectId || undefined } as any),
    enabled: canReadUsers,
    staleTime: 3e5,
  });

  const { data: personnel } = useQuery({
    queryKey: ['personnel', 'partyPicker'],
    queryFn: () => personnelLookupService.getAll(),
    enabled: canReadPersonnel,
    staleTime: 3e5,
  });

  const { data: listsPage } = useQuery({
    queryKey: ['recipientLists', 'partyPicker'],
    queryFn: () => recipientListsService.list({ skip: 0, limit: 200 }),
    // Never needed inside a list — nesting is refused — so it is not fetched there.
    enabled: canReadLists && context !== 'list',
    staleTime: 3e5,
  });

  // Personnel come from a cached lookup with no project filter of its own, so
  // the same rule is applied here instead.
  // A user is identified by `userId`, but that is optional on the record, so the
  // email and finally the raw id stand in. A picker entry with no label at all
  // is worse than an ugly one.
  const userOptions = ((usersPage as any)?.data || []).map((u: any) => ({
    value: u._id,
    label: u.userId || u.contact?.email || u._id,
  }));

  const personnelOptions = (personnel || []).filter(referencableIn(projectId)).map((p) => ({
    value: p._id,
    label: p.userId ? `${p.name} (${p.userId})` : p.name || p._id,
  }));

  const listOptions = ((listsPage as any)?.data || [])
    // A deactivated list is refused as a new reference, so it is not offered.
    .filter((l: any) => l.isActive !== false)
    // Nor is another project's: the save would refuse it as out of scope.
    .filter(referencableIn(projectId))
    .map((l: any) => ({ value: l._id, label: l.recipientListCode }));

  const partyTypeOptions = (partyVS?.items || [])
    .map((i: any) => ({ value: i.code, label: i.label }))
    .filter((o: any) => !(context === 'list' && o.value === 'LIST'));

  const update = (index: number, next: Party) =>
    onChange(value.map((p, i) => (i === index ? next : p)));

  // Changing the type clears the old identifier. Carrying it over would leave a
  // party naming a user AND a list, which the API refuses.
  const changeType = (index: number, partyType: string) =>
    update(index, { partyType, ...(partyType === 'EXTERNAL' ? { contact: {} } : {}) });

  const picker = (
    index: number, party: Party, field: string,
    options: any[], allowed: boolean, placeholder: string,
  ) => {
    if (!allowed) {
      return (
        <InputText
          value={(party as any)[field] || ''}
          placeholder={`${field} (no permission to browse)`}
          className="flex-1 font-mono text-xs"
          data-testid={`${testId}.Id.${index}`}
          onChange={(e) => update(index, { ...party, [field]: e.target.value })}
        />
      );
    }
    return (
      <Dropdown
        value={(party as any)[field] || null}
        options={options}
        optionLabel="label"
        optionValue="value"
        filter
        showClear
        placeholder={placeholder}
        emptyMessage="None available"
        className="flex-1"
        data-testid={`${testId}.Id.${index}`}
        onChange={(e) => update(index, { ...party, [field]: e.value || '' })}
      />
    );
  };

  const identifierFor = (party: Party, index: number) => {
    if (party.partyType === 'EXTERNAL') {
      return (
        <div className="flex gap-2 flex-1">
          <InputText value={party.contact?.email || ''} placeholder="email"
            className="flex-1" data-testid={`${testId}.Email.${index}`}
            onChange={(e) => update(index, { ...party, contact: { ...party.contact, email: e.target.value } })} />
          <InputText value={party.contact?.phone || ''} placeholder="phone"
            className="flex-1" data-testid={`${testId}.Phone.${index}`}
            onChange={(e) => update(index, { ...party, contact: { ...party.contact, phone: e.target.value } })} />
        </div>
      );
    }
    if (party.partyType === 'PERSONNEL') {
      return picker(index, party, 'personnelMasterId', personnelOptions, canReadPersonnel, 'Select personnel');
    }
    if (party.partyType === 'LIST') {
      return picker(index, party, 'recipientListId', listOptions, canReadLists, 'Select a list');
    }
    return picker(index, party, 'userId', userOptions, canReadUsers, 'Select a user');
  };

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        {hint ? <span className="block text-xs text-gray-400">{hint}</span> : null}
      </div>

      {value.length === 0 && (
        <p className="text-xs text-gray-400">None yet — add one below.</p>
      )}

      {value.map((party, index) => (
        <div key={index} className="flex items-center gap-2">
          <Dropdown value={party.partyType} options={partyTypeOptions} optionLabel="label" optionValue="value"
            onChange={(e) => changeType(index, e.value)} className="w-40" data-testid={`${testId}.Type.${index}`} />
          {identifierFor(party, index)}
          <button aria-label="Remove" type="button" title="Remove"
            className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
            onClick={() => onChange(value.filter((_, i) => i !== index))}>
            <i className="pi pi-trash text-red-600 dark:text-red-400" />
          </button>
        </div>
      ))}

      <div>
        <Button type="button" label="Add party" icon="pi pi-plus" size="small" severity="secondary" outlined
          onClick={() => onChange([...value, { ...BLANK }])} data-testid={`${testId}.Add`} />
      </div>
    </div>
  );
};

export default PartyEditor;
